import create from 'zustand';
import shallow from 'zustand/shallow';
import { buildGridRows } from './helpers/buildGridRows3';
import { useEffect, useMemo } from 'react';
import { Activity, ApprovalState, TimeEntryDto, TimeEntryApprovalDto, ProjectApprovalData } from '../api/generated/data-contracts';
import { APPROVAL_STATES, CUSTOM_RATES } from '../_pages/ProjectApproval/components/Grid/CONSTANTS';
import produce from 'immer';

type NonFunctionPropertyNames<T> = {
    [K in keyof T]: T[K] extends (...args: any[]) => any ? never : K;
}[keyof T];

type NonFunctionProperties<T> = Pick<T, NonFunctionPropertyNames<T>>;

export type PseudoRowType = 'project' | 'user' | 'group';

export type PseudoRow = {
    id: string;
    name: string;
    timeEntry: BigHours<TimeEntryDto>;
    parentId?: string;
    pseudoRow: true;
    type: PseudoRowType;
    projectId: string;
    taskId: string;
};

type BigHours<T extends { hours: number }> = Omit<T, 'hours'> & { hours: Big };

export type TimeEntryApprovalDtoState = Omit<TimeEntryApprovalDto, 'timeEntry' | 'approvalState'> & {
    timeEntry: BigHours<TimeEntryDto>;
    name: string;
    type: 'approval';
    parentId: string;
};
export type TimeRow = TimeEntryApprovalDtoState | PseudoRow;

export type ExpandedRows = {
    [key: string]: boolean;
};

type FunctionOr<T> = T | ((v: T) => T);

export type StateSetter<T> = (valueOrUpdate: FunctionOr<T>) => void;

type ApprovalStoreSetter<T extends keyof ApprovalStoreValues> = StateSetter<ApprovalStore[T]>;

type ApprovalStore = {
    selectedProjectIds: string[];
    setSelectedProjectIds: ApprovalStoreSetter<'selectedProjectIds'>;
    selectedUserIds: string[];
    setSelectedUserIds: ApprovalStoreSetter<'selectedUserIds'>;
    selectedActivityIds: string[];
    setSelectedActivityIds: ApprovalStoreSetter<'selectedActivityIds'>;
    setApprovals: ApprovalStoreSetter<'approvals'>;
    initialData: ProjectApprovalData | null;

    rows: TimeRow[];
    setRows: ApprovalStoreSetter<'rows'>;
    initState: (data: ProjectApprovalData) => void;
    /**
     *  Nukes the state
     */
    cleanup: () => void;
    expandedRows: ExpandedRows;
    setExpandedRows: ApprovalStoreSetter<'expandedRows'>;
    setGridExpandedRow: (rowId: string, expand: boolean) => void;
    selectedRows: Set<string>;
    setSelectedRows: ApprovalStoreSetter<'selectedRows'>;
    selectedApprovalStates: ApprovalState[];
    setSelectedApprovalStates: ApprovalStoreSetter<'selectedApprovalStates'>;
} & ProjectApprovalData;

type ApprovalStoreValues = NonFunctionProperties<ApprovalStore>;

export const useApprovalStore = create<ApprovalStore>((set, get, api) => {
    return {
        initialData: null,
        rows: [],
        setRows: valueOrUpdater => {
            set(store => ({ ...store, rows: unwrap(valueOrUpdater, store.rows) }));
        },
        projects: {},
        users: {},
        activities: {},
        approvals: {},
        setApprovals: valueOrUpdater => {
            set(store => ({ ...store, approvals: unwrap(valueOrUpdater, store.approvals) }));
        },
        tasks: {},
        selectedApprovalStates: [...APPROVAL_STATES],
        setSelectedApprovalStates: valueOrUpdater => {
            set(store => {
                const nextState = unwrap(valueOrUpdater, store.selectedApprovalStates);
                const filteredApprovals = Object.fromEntries(
                    Object.entries(store.approvals).filter(
                        ([id, row]) =>
                            nextState.includes(row.approvalState) &&
                            store.selectedProjectIds.includes(row.projectId) &&
                            store.selectedUserIds.includes(row.userId) &&
                            handleActivityFilter(store.selectedActivityIds, row),
                    ),
                );
                return {
                    ...store,
                    selectedApprovalStates: nextState,
                    rows: buildGridRows({
                        activities: store.activities,
                        approvals: filteredApprovals,
                        projects: store.projects,
                        tasks: store.tasks,
                        users: store.users,
                    }),
                    selectedRows: new Set(),
                };
            });
        },
        selectedProjectIds: [],
        setSelectedProjectIds: valueOrUpdater => {
            set(store => {
                const nextState = unwrap(valueOrUpdater, store.selectedProjectIds);
                const filteredApprovals = Object.fromEntries(
                    Object.entries(store.approvals).filter(
                        ([id, row]) =>
                            nextState.includes(row.projectId) &&
                            store.selectedUserIds.includes(row.userId) &&
                            store.selectedApprovalStates.includes(row.approvalState) &&
                            handleActivityFilter(store.selectedActivityIds, row),
                    ),
                );
                return {
                    ...store,
                    selectedProjectIds: nextState,
                    rows: buildGridRows({
                        activities: store.activities,
                        approvals: filteredApprovals,
                        projects: store.projects,
                        tasks: store.tasks,
                        users: store.users,
                    }),
                    selectedRows: new Set(),
                };
            });
        },
        selectedUserIds: [],
        setSelectedUserIds: valueOrUpdater => {
            set(store => {
                const nextState = unwrap(valueOrUpdater, store.selectedUserIds);
                const filteredApprovals = Object.fromEntries(
                    Object.entries(store.approvals).filter(
                        ([id, row]) =>
                            nextState.includes(row.userId) &&
                            store.selectedProjectIds.includes(row.projectId) &&
                            store.selectedApprovalStates.includes(row.approvalState) &&
                            handleActivityFilter(store.selectedActivityIds, row),
                    ),
                );
                return {
                    ...store,
                    selectedUserIds: nextState,
                    rows: buildGridRows({
                        activities: store.activities,
                        approvals: filteredApprovals,
                        projects: store.projects,
                        tasks: store.tasks,
                        users: store.users,
                    }),
                    selectedRows: new Set(),
                };
            });
        },
        selectedActivityIds: [],
        setSelectedActivityIds: valueOrUpdater => {
            set(store => {
                const nextState = unwrap(valueOrUpdater, store.selectedActivityIds);
                const filteredApprovals = Object.fromEntries(
                    Object.entries(store.approvals).filter(
                        ([id, row]) =>
                            handleActivityFilter(nextState, row) &&
                            store.selectedProjectIds.includes(row.projectId) &&
                            store.selectedUserIds.includes(row.userId) &&
                            store.selectedApprovalStates.includes(row.approvalState),
                    ),
                );
                return {
                    ...store,
                    selectedActivityIds: nextState,
                    rows: buildGridRows({
                        activities: store.activities,
                        approvals: filteredApprovals,
                        projects: store.projects,
                        tasks: store.tasks,
                        users: store.users,
                    }),
                    selectedRows: new Set(),
                };
            });
        },
        initState: data => {
            const rows = buildGridRows(data);
            const state: ApprovalStoreValues = {
                // ...data,
                initialData: data,
                rows,
                expandedRows: rows.reduce((acc, row) => {
                    acc[row.id] = false;
                    return acc;
                }, {} as ExpandedRows),
                selectedRows: new Set(),
                selectedProjectIds: Object.keys(data.projects),
                selectedUserIds: Object.keys(data.users),
                selectedActivityIds: [...Object.keys(data.activities), CUSTOM_RATES],
                selectedApprovalStates: [...APPROVAL_STATES],
                activities: data.activities,
                approvals: data.approvals,
                projects: data.projects,
                tasks: data.tasks,
                users: data.users,
            };
            set(state);
        },
        cleanup: () => {
            const state: ApprovalStoreValues = {
                initialData: null,
                rows: [],
                activities: {},
                approvals: {},
                projects: {},
                tasks: {},
                users: {},
                expandedRows: {},
                selectedRows: new Set(),
                selectedProjectIds: [],
                selectedUserIds: [],
                selectedActivityIds: [],
                selectedApprovalStates: [...APPROVAL_STATES],
            };
            set(state);
        },
        expandedRows: {},
        setExpandedRows: valueOrUpdater =>
            set(store => ({
                ...store,
                expandedRows: unwrap(valueOrUpdater, store.expandedRows),
            })),
        setGridExpandedRow: (id, expand) => {
            set(store => {
                if (expand) {
                    const parentRows = getParentRows(id, store.rows);
                    const subrowFamily = getFamilyOfSubrows(id, store.rows);
                    return {
                        ...store,
                        expandedRows: [...parentRows, ...subrowFamily].reduce(
                            (newState, row) => {
                                newState[row.id] = true;
                                return newState;
                            },
                            { ...store.expandedRows, [id]: true } as ExpandedRows,
                        ),
                    };
                }
                const subrowFamily = getFamilyOfSubrows(id, store.rows);
                return {
                    ...store,
                    expandedRows: subrowFamily.reduce(
                        (newState, row) => {
                            newState[row.id] = false;
                            return newState;
                        },
                        { ...store.expandedRows, [id]: false } as ExpandedRows,
                    ),
                };
            });
        },
        selectedRows: new Set(),
        setSelectedRows: valueOrUpdate => {
            set(store => {
                return {
                    ...store,
                    selectedRows: unwrap(valueOrUpdate, store.selectedRows),
                };
            });
        },
    };
});

const handleActivityFilter = (activityIds: string[], row: TimeEntryApprovalDto) => {
    return (activityIds.includes(CUSTOM_RATES) && !row.activityId) || activityIds.includes(row.activityId);
};

const unwrap = <T>(valueOrUpdater: FunctionOr<T>, currentState: T) => (valueOrUpdater instanceof Function ? valueOrUpdater(currentState) : valueOrUpdater);

export const getParentRows = (rowId: string, rows: TimeRow[]) => {
    const tree: TimeRow[] = [];
    let currentAncestor = rows.find(row => row.id === rowId);
    while (currentAncestor) {
        currentAncestor = rows.find(row => row.id === currentAncestor.parentId);
        if (currentAncestor) {
            tree.push(currentAncestor);
        }
    }
    return tree;
};

export const getFamilyOfSubrows = (rowId: string, rows: TimeRow[]) => {
    const subrowFamily: TimeRow[] = [];
    const recurse = (rowId: string) => {
        const subrows = getImmediateSubrows(rowId, rows);
        subrows.forEach(subrow => {
            subrowFamily.push(subrow);
            recurse(subrow.id);
        });
    };
    recurse(rowId);
    return subrowFamily;
};

const isRootRow = (row: TimeRow) => {
    return !row.parentId;
};

export const getImmediateSubrows = (rowId: string, rows: TimeRow[]) => {
    return rows.filter(row => row.parentId === rowId);
};

export const isPseudoRow = (row: TimeRow): row is PseudoRow => Boolean((row as any).pseudoRow);
export const isApprovalRow = (row: TimeRow): row is TimeEntryApprovalDtoState => !(row as any).pseudoRow;

export const useGridRenderableRows = (rows: (TimeEntryApprovalDtoState | PseudoRow)[]) => {
    const expandedRows = useApprovalStore(store => store.expandedRows, shallow);
    return useMemo(() => {
        // The row is renderable if it is a root row or the parent is expanded
        return rows.filter(row => {
            return isRootRow(row) || expandedRows[row.parentId];
        });
    }, [expandedRows, rows]);
};

export const useInitializeApprovalStore = (data?: ProjectApprovalData) => {
    const { initState, cleanup, rows } = useApprovalStore(
        store => ({
            initState: store.initState,
            cleanup: store.cleanup,
            rows: store.rows,
        }),
        shallow,
    );

    useEffect(() => {
        if (data) {
            initState(data);
        }
    }, [data, initState]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return rows;
};

export const useGridExpandedRowState = (id: string) =>
    useApprovalStore(
        store =>
            [
                store.expandedRows[id],
                (valueOrUpdater: FunctionOr<boolean>) => {
                    const nextState = unwrap(valueOrUpdater, store.expandedRows[id]);
                    const subrows = getFamilyOfSubrows(id, store.rows);
                    store.setExpandedRows(lastState => {
                        return subrows.reduce(
                            (next, row) => {
                                next[row.id] = nextState;
                                return next;
                            },
                            { ...lastState, [id]: nextState } as ExpandedRows,
                        );
                    });
                },
            ] as const,
        shallow,
    );

export const useSelectedRowIdState = (id: string) =>
    useApprovalStore(
        store =>
            [
                store.selectedRows.has(id),
                (valueOrUpdater: FunctionOr<boolean>) => {
                    const nextState = unwrap(valueOrUpdater, store.selectedRows.has(id));
                    const subrowIds = getFamilyOfSubrows(id, store.rows).map(row => row.id);
                    store.setSelectedRows(rows => {
                        if (nextState) {
                            return new Set([...rows, id, ...subrowIds]);
                        }
                        return new Set([...rows].filter(rowId => rowId !== id && !subrowIds.includes(rowId)));
                    });
                },
            ] as const,
        shallow,
    );

export const useApprovalState = (rowId: string) =>
    useApprovalStore(store => {
        const currentState = store.approvals[rowId].approvalState;
        return [
            currentState,
            (valueOrUpdater: FunctionOr<ApprovalState>) => {
                store.setApprovals(
                    produce(approvals => {
                        approvals[rowId].approvalState = unwrap(valueOrUpdater, approvals[rowId].approvalState);
                    }),
                );
            },
        ] as const;
    }, shallow);

export const useActivityState = (rowId: string) =>
    useApprovalStore(store => {
        const row = store.approvals[rowId];
        const currentState = store.activities[row.activityId];
        return [
            currentState,
            (valueOrUpdater: FunctionOr<Activity>) => {
                const nextActivityState = unwrap(valueOrUpdater, currentState);
                store.setApprovals(
                    produce(approvals => {
                        approvals[rowId].activityId = nextActivityState?.id;
                    }),
                );
            },
        ] as const;
    }, shallow);

export const useRateState = (rowId: string) =>
    useApprovalStore(store => {
        const row = store.approvals[rowId];
        const currentState = store.activities[row.activityId]?.rate ?? row.rate ?? row.originalRate;
        return [
            currentState,
            (valueOrUpdater: FunctionOr<number | null | undefined>) => {
                store.setApprovals(
                    produce(approvals => {
                        approvals[rowId].rate = unwrap(valueOrUpdater, currentState);
                    }),
                );
            },
        ] as const;
    }, shallow);

export const useCurrencyRate = (rowId: string) =>
    useApprovalStore(store => {
        const row = store.approvals[rowId];
        const activity = store.activities[row.activityId];
        // If an activity is selected, use its rate; if the activity has a null rate, fall back to the approval/assignment rate
        const currentState = activity ? activity.rate ?? row.rate ?? row.originalRate ?? '' : row.rate ?? row.originalRate ?? '';
        if (currentState === null) {
            return '';
        }
        const currencyCode = activity?.rateCurrencyCode ?? row.originalRateCurrencyCode ?? '';
        return `${typeof currentState === 'number' ? currentState.toFixed(2) : currentState} ${currencyCode}`.trim();
    }, shallow);

export const useMissingApprovalCount = (rowId: string) =>
    useApprovalStore(store => {
        const row = store.rows.find(row => row.id === rowId);
        const parentRows = getParentRows(row.id, store.rows);
        const siblingRows = (parentRows.length && getFamilyOfSubrows(parentRows[parentRows.length - 1].id, store.rows)) || [];
        const siblingApprovalRows = siblingRows.filter(isApprovalRow);
        return siblingApprovalRows.filter(row => {
            return store.approvals[row.id].approvalState !== 'Approved';
        }).length;
    }, shallow);
